import { useCallback } from 'react';

import type { MetagamesCurrentEvent, MetagamesEventByCoin } from '../../../dto/metagames.types';
import { MetaGamesEventType } from '../../../dto/metagames.types';
import { useStores } from '../../../stores/useStores';
import { devError } from '../../../utils/log';
import {
  formatFeatureBalance,
  metagameBalance,
} from '../components/MetagameWidget/MetagameFeatureBalance/metagameFeatureBalance';

interface UseMetagameWidgetDataProps {
  event: MetagamesCurrentEvent;
  eventByCoin?: MetagamesEventByCoin;
}

export function useMetagameWidgetData(props: UseMetagameWidgetDataProps) {
  const { event, eventByCoin } = props;
  const {
    metagamesStore,
    hammersStore,
    puzzleStore,
    prizeDropStore,
    trailStore,
    plinkoStore,
    rouletteStore,
    cardWarStore,
  } = useStores();

  const widgetImageUrl = eventByCoin ? eventByCoin.widgetImageUrl : undefined;
  const balanceValue = eventByCoin ? formatFeatureBalance(metagameBalance(eventByCoin)) : '';

  const handleClick = () => {
    if (!eventByCoin) {
      devError('MetagamesWidget onClickHandler error: eventByCoin is undefined');
      return;
    }

    const analyticsParams = {
      event_action: 'click',
      triggered_by: 'click',
      click_location: 'widget',
    } as const;

    let feature_sub_type: 'prizeDrop' | 'hammers' | 'puzzle' | 'trail' | 'plinko' | 'roulette' | 'cardWar';

    switch (event.typeName) {
      case MetaGamesEventType.HAMMERS:
        void hammersStore.startHammersGame();
        feature_sub_type = 'hammers';
        break;
      case MetaGamesEventType.PUZZLE:
        void puzzleStore.startPuzzleGame();
        feature_sub_type = 'puzzle';
        break;
      case MetaGamesEventType.PRIZE_DROP:
        void prizeDropStore.startPrizeDropGame({ type: 'info' });
        feature_sub_type = 'prizeDrop';
        break;
      case MetaGamesEventType.TRAIL:
        void trailStore.startTrailGame();
        feature_sub_type = 'trail';
        break;
      case MetaGamesEventType.PLINKO:
        void plinkoStore.startPlinkoGame();
        feature_sub_type = 'plinko';
        break;
      case MetaGamesEventType.ROULETTE:
        void rouletteStore.startRouletteGame();
        feature_sub_type = 'roulette';
        break;
      case MetaGamesEventType.CARD_WAR:
        void cardWarStore.startCardWarGame();
        feature_sub_type = 'cardWar';
        break;
      default:
        devError(`MetagamesWidget onClickHandler error: Unknown event type ${event.typeName}`);
        return;
    }

    void metagamesStore.sendAnalyticsEvent({ ...analyticsParams, feature_sub_type });
  };

  const onTimerEnd = useCallback(() => {
    void metagamesStore.fetch();
  }, [metagamesStore]);

  return { widgetImageUrl, balanceValue, handleClick, onTimerEnd };
}
